class CartFormModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.boundKeyHandler = this.handleKeyDown.bind(this);
    this.boundSubmitHandler = this.handleSubmit.bind(this);
    this.productTags = [];
    this.currentItemKey = null;
    this.variantTitle = null; // Store variant title for question count
    this.init();
  }

  // Returns the product type based on tags and variant.
  // Supports both original tags (spell_casting, custom_spell, cbtm_spell) 
  // and WWWBF-safe alternatives (ritual_service, custom_ritual, cbtm_ritual)
  getProductType(variantTitle = null) {
    const tags = this.productTags;
    const variant = variantTitle || this.variantTitle;
    
    if (tags.includes('spell_casting') || tags.includes('ritual_service')) {
      if (tags.includes('custom_spell') || tags.includes('custom_ritual')) {
        return 'custom_spell';
      } else if (tags.includes('cbtm_spell') || tags.includes('cbtm_ritual')) {
        return 'cbtm_spell';
      }
      return 'spell';
    } else if (tags.includes('tarot_reading')) {
      // Get question count based on variant if provided
      const questionCount = this.getQuestionCountForVariant(variant);
      return { type: 'tarot', questionCount: questionCount };
    }
    return 'regular';
  }

  /**
   * Get question count for a specific variant
   * Standard = 1 question, Premium/VIP = 3 questions
   * Falls back to product tag if variant doesn't match
   */
  getQuestionCountForVariant(variantTitle = null) {
    if (variantTitle) {
      const variantTitleLower = variantTitle.toLowerCase().trim();
      if (variantTitleLower === 'standard') {
        return 1;
      } else if (variantTitleLower === 'premium' || variantTitleLower === 'vip') {
        return 3;
      }
    }
    
    // Fallback to product tag
    const questionTag = this.productTags.find(tag => tag.startsWith('questions_'));
    return questionTag ? parseInt(questionTag.split('_')[1]) : 1;
  }

  // Creates additional fields for tarot reading.
  createTarotFields(questionCount, savedValues = {}) {
    // Validate questionCount
    if (!questionCount || questionCount < 1 || questionCount > 3) {
      questionCount = 1; // Default to 1 if invalid
    }
    
    let questionFields = '';
    for (let i = 1; i <= questionCount; i++) {
      const savedValue = savedValues[i] || '';
      // Escape HTML to prevent XSS
      const escapedValue = this.escapeHtml(savedValue);
      questionFields += `
        <div class="form-row">
          <label for="question${i}">Question ${i}</label>
          <textarea 
            id="question${i}" 
            name="question${i}"
            required
            minlength="10"
            maxlength="250"
            placeholder="Enter your question..."
          >${escapedValue}</textarea>
          <div class="error-message" id="questionError${i}">Question must be between 10 and 250 characters</div>
        </div>
      `;
    }
    return questionFields;
  }

  /**
   * Escape HTML to prevent XSS attacks
   */
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  init() {
    // Parse product tags from the data attribute.
    try {
      const tagsAttr = this.getAttribute('data-product-tags');
      this.productTags = tagsAttr ? JSON.parse(tagsAttr) : [];
    } catch (e) {
      console.error('Error parsing product tags:', e);
      this.productTags = [];
    }

    // Attach shadow DOM.
    this.attachShadow({ mode: 'open' });

    // Minimal styles needed for the modal.
    const style = document.createElement('style');
    style.textContent = `
      .cart-form-modal {
        display: flex;
        opacity: 0;
        visibility: hidden;
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        z-index: 999999;
        background: rgba(0, 0, 0, 0.7);
        transition: opacity 0.4s ease-out, visibility 0.4s ease-out;
        align-items: center;
        justify-content: center;
      }
      .cart-form-modal.active {
        opacity: 1;
        visibility: visible;
      }
      .cart-form-modal__content {
        background: black;
        width: 95%;
        max-width: 500px;
        margin: 0 auto;
        padding: 20px;
        border-radius: 8px;
        position: relative;
        max-height: 90vh;
        overflow-y: auto;
      }
      .close-button {
        position: absolute;
        top: 8px;
        right: 8px;
        background: none;
        border: none;
        font-size: 24px;
        color: white;
        cursor: pointer;
      }
      .form-header {
        text-align: center;
        margin-bottom: 16px;
      }
      .form-title {
        font-family: 'cinzeldecorative';
        font-size: 20px;
        font-weight: 500;
        color: #adbf55;
        margin: 0;
      }
      .form-subtitle {
        font-size: 16px;
        color: white;
        margin: 4px 0 0;
      }
      .form-row {
        margin-bottom: 12px;
      }
      .form-row label {
        display: block;
        font-weight: 500;
        color: white;
        margin-bottom: 4px;
      }
      .form-row input,
      .form-row textarea {
        width: 100%;
        padding: 8px;
        border: 2px solid #E5E7EB;
        border-radius: 4px;
        font-size: 16px;
        box-sizing: border-box;
      }
      .form-row input:focus,
      .form-row textarea:focus {
        outline: none;
        border-color: #666;
      }
      .error-message {
        color: #ff4444;
        font-size: 14px;
        margin-top: 4px;
        display: none;
      }
      .error-message.show {
        display: block;
      }
      .submit-button {
        width: 100%;
        padding: 12px;
        color: white;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        
        /* Your custom fading black background */
        background: linear-gradient(90deg, 
          rgba(0, 0, 0, 0.1),
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 1), 
          rgb(0, 0, 0), 
          rgba(0, 0, 0, 1), 
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 0.1)
        );
        
        /* Gold gradient borders - TOP and BOTTOM only */
        border-left: none;
        border-right: none;
        border-top: 1px solid transparent;
        border-bottom: 1px solid transparent;
        border-image: linear-gradient(90deg, 
          rgba(184, 148, 28, 0.2),
          rgba(212, 175, 55, 0.7),
          rgba(230, 201, 101, 1),
          rgba(212, 175, 55, 0.7),
          rgba(184, 148, 28, 0.2)
        ) 1;
        
        /* Sharp edges (no rounded corners) */
        border-radius: 0px;
        
        /* Subtle gold glow - LEFT and RIGHT sides only */
        box-shadow: 
          -4px 0 6px rgba(212, 175, 55, 0.15),
          4px 0 6px rgba(212, 175, 55, 0.15);
      }
      .submit-button:hover {
        /* Brighter gold borders on hover */
        background: linear-gradient(90deg, 
          rgba(0, 0, 0, 0.1),
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 1), 
          rgb(0, 0, 0), 
          rgba(0, 0, 0, 1), 
          rgba(0, 0, 0, 0.6), 
          rgba(0, 0, 0, 0.1)
        );
        
        border-image: linear-gradient(90deg, 
          rgba(184, 148, 28, 0.5),
          rgba(212, 175, 55, 1),
          rgba(230, 201, 101, 1),
          rgba(212, 175, 55, 1),
          rgba(184, 148, 28, 0.5)
        ) 1;
        
        /* Intensified side glow on hover */
        box-shadow: 
          -6px 0 8px rgba(212, 175, 55, 0.25),
          6px 0 8px rgba(212, 175, 55, 0.25);
        
        /* Subtle lift */
        transform: translateY(-2px);
      }
      .submit-button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        background: #999;
        border-image: none;
        box-shadow: none;
      }
      .spinner {
        display: none;
        width: 20px;
        height: 20px;
        border: 2px solid #fff;
        border-radius: 50%;
        border-top-color: transparent;
        animation: spin 0.8s linear infinite;
        margin-right: 8px;
      }
      .submit-button.loading .spinner {
        display: inline-block;
      }
      @keyframes spin {
        100% { transform: rotate(360deg); }
      }
    `;

    // Generate the form fields based on product type.
    const productType = this.getProductType();
    let formFields = '';
    if (
      productType === 'spell' ||
      productType === 'custom_spell' ||
      productType === 'cbtm_spell' ||
      productType === 'regular'
    ) {
      formFields = `
        <div class="form-row">
          <label for="customerName">Your Name</label>
          <input 
            type="text" 
            id="customerName" 
            name="customerName" 
            required 
            maxlength="100" 
            autocomplete="name" 
            placeholder="Enter your full name"
          >
          <div class="error-message" id="nameError">Please enter your name</div>
        </div>
        <div class="form-row">
          <label for="customerDOB">Date of Birth</label>
          <input 
            type="text" 
            id="customerDOB" 
            name="customerDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="dobError">Invalid date format</div>
        </div>
        <div class="form-row">
          <label for="targetName">Person of Interest</label>
          <input 
            type="text" 
            id="targetName" 
            name="targetName" 
            maxlength="100" 
            placeholder="Enter their full name"
          >
          <div class="error-message" id="targetNameError"></div>
        </div>
        <div class="form-row">
          <label for="targetDOB">Date of Birth</label>
          <input 
            type="text" 
            id="targetDOB" 
            name="targetDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="targetDobError">Invalid date format</div>
        </div>
        <div class="form-row">
          <label for="additionalDetails">Additional Details</label>
          <textarea 
            id="additionalDetails" 
            name="additionalDetails" 
            maxlength="500" 
            placeholder="Any specific details or requests..."
          ></textarea>
          <div class="error-message" id="detailsError"></div>
        </div>
      `;
    } else if (typeof productType === 'object' && productType.type === 'tarot') {
      formFields = `
        <div class="form-row">
          <label for="customerName">Your Name</label>
          <input 
            type="text" 
            id="customerName" 
            name="customerName" 
            required 
            maxlength="100" 
            autocomplete="name" 
            placeholder="Enter your full name"
          >
          <div class="error-message" id="nameError">Please enter your name</div>
        </div>
        <div class="form-row">
          <label for="customerDOB">Date of Birth</label>
          <input 
            type="text" 
            id="customerDOB" 
            name="customerDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="dobError">Invalid date format</div>
        </div>
        <div class="form-row">
          <label for="targetName">Person of Interest</label>
          <input 
            type="text" 
            id="targetName" 
            name="targetName" 
            maxlength="100" 
            placeholder="Enter their full name"
          >
          <div class="error-message" id="targetNameError"></div>
        </div>
        <div class="form-row">
          <label for="targetDOB">Date of Birth</label>
          <input 
            type="text" 
            id="targetDOB" 
            name="targetDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="targetDobError">Invalid date format</div>
        </div>
        ${this.createTarotFields(productType.questionCount, {})}
      `;
    }

    // Combine header and form into the modal HTML.
    const modalHtml = `
      <div id="CartFormModal" class="cart-form-modal" role="dialog" aria-modal="true">
        <div class="cart-form-modal__content">
          <button class="close-button" aria-label="Close">✕</button>
          <div class="form-header">
            <h2 id="modalTitle" class="form-title">Edit Details</h2>
            <p class="form-subtitle">Please update your personalization details</p>
          </div>
          <form id="CustomCartForm" novalidate>
            ${formFields}
            <button type="submit" class="submit-button">
              <span class="spinner" aria-hidden="true"></span>
              <span class="button-text">Update Cart</span>
            </button>
          </form>
        </div>
      </div>
    `;

    // Insert the style and HTML into the shadow DOM.
    this.shadowRoot.innerHTML = `${style.outerHTML}${modalHtml}`;
    this.modal = this.shadowRoot.getElementById('CartFormModal');
    this.closeButton = this.shadowRoot.querySelector('.close-button');
    this.customForm = this.shadowRoot.getElementById('CustomCartForm');
    this.submitButton = this.shadowRoot.querySelector('.submit-button');

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Close modal when close button is clicked or when clicking outside the content.
    this.closeButton.addEventListener('click', () => this.hideModal());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hideModal();
      }
    });

    // Handle form submission.
    this.customForm.addEventListener('submit', this.boundSubmitHandler);

    // Prevent background scrolling when touching the modal.
    this.modal.addEventListener('touchmove', (e) => {
      if (e.target === this.modal) {
        e.preventDefault();
      }
    }, { passive: false });

    // Add simple date formatting/validation listeners.
    const dobInputs = this.shadowRoot.querySelectorAll('input[placeholder="DD/MM/YYYY"]');
    dobInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 8) value = value.slice(0, 8);
        if (value.length >= 4) {
          value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
        } else if (value.length >= 2) {
          value = `${value.slice(0, 2)}/${value.slice(2)}`;
        }
        e.target.value = value;
        this.validateDateInput(input);
      });
    });
  }

  validateDateInput(input) {
    if (input.value.length !== 10) {
      input.classList.remove('invalid');
      return;
    }
    const parts = input.value.split('/');
    if (parts.length !== 3) {
      input.classList.add('invalid');
      return;
    }
    const [p1, p2, p3] = parts.map(Number);
    const year = p3;
    if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
      input.classList.add('invalid');
      return;
    }
    const A = p1;
    const B = p2;
    const option1 = A >= 1 && A <= 31 && B >= 1 && B <= 12;
    const option2 = A >= 1 && A <= 12 && B >= 1 && B <= 31;
    if (option1 || option2) {
      input.classList.remove('invalid');
    } else {
      input.classList.add('invalid');
    }
  }

  connectedCallback() {
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.customForm.removeEventListener('submit', this.boundSubmitHandler);
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.hideModal();
    }
  }

  validateDate(dateString) {
    if (!dateString) return false;
    const parts = dateString.split('/');
    if (parts.length !== 3) return false;

    const [p1, p2, p3] = parts.map(Number);
    const year = p3;
    if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
      return false;
    }

    const A = p1;
    const B = p2;
    const option1 = A >= 1 && A <= 31 && B >= 1 && B <= 12;
    const option2 = A >= 1 && A <= 12 && B >= 1 && B <= 31;
    return option1 || option2;
  }

  sanitizeInput(input) {
    return input ? input.toString().replace(/[<>]/g, '').trim() : '';
  }

  async handleSubmit(e) {
    e.preventDefault();

    const productType     = this.getProductType();
    const nameInput       = this.shadowRoot.getElementById('customerName');
    const dobInput        = this.shadowRoot.getElementById('customerDOB');
    const targetNameInput = this.shadowRoot.getElementById('targetName');
    const targetDobInput  = this.shadowRoot.getElementById('targetDOB');
    const detailsInput    = this.shadowRoot.getElementById('additionalDetails');

    // Reset error messages and invalid classes
    this.shadowRoot.querySelectorAll('.error-message.show')
      .forEach(error => error.classList.remove('show'));
    this.shadowRoot.querySelectorAll('.invalid')
      .forEach(field => field.classList.remove('invalid'));

    let isValid = true;

    // 1) Validate “Your Name”
    if (!nameInput.value.trim() || nameInput.value.length > 100) {
      this.shadowRoot.getElementById('nameError').classList.add('show');
      nameInput.classList.add('invalid');
      nameInput.focus();
      isValid = false;
    }

    // 2) Validate “Date of Birth” if present
    if (dobInput.value && !this.validateDate(dobInput.value)) {
      this.shadowRoot.getElementById('dobError').classList.add('show');
      dobInput.classList.add('invalid');
      if (isValid) dobInput.focus();
      isValid = false;
    }

    // 3) Validate “Person DOB” if present
    if (targetDobInput.value && !this.validateDate(targetDobInput.value)) {
      this.shadowRoot.getElementById('targetDobError').classList.add('show');
      targetDobInput.classList.add('invalid');
      if (isValid) targetDobInput.focus();
      isValid = false;
    }

    // 4) If tarot, validate questions - use current variant's question count
    const currentProductType = this.getProductType(this.variantTitle);
    if (typeof currentProductType === 'object' && currentProductType.type === 'tarot') {
      const firstQuestion = this.shadowRoot.getElementById('question1');
      if (!firstQuestion.value.trim() || firstQuestion.value.length < 10 || firstQuestion.value.length > 250) {
        this.shadowRoot.getElementById('questionError1').classList.add('show');
        firstQuestion.classList.add('invalid');
        if (isValid) firstQuestion.focus();
        isValid = false;
      }
      for (let i = 2; i <= currentProductType.questionCount; i++) {
        const questionInput = this.shadowRoot.getElementById(`question${i}`);
        if (
          questionInput.value.trim() &&
          (questionInput.value.length < 10 || questionInput.value.length > 250)
        ) {
          this.shadowRoot.getElementById(`questionError${i}`).classList.add('show');
          questionInput.classList.add('invalid');
          if (isValid) questionInput.focus();
          isValid = false;
        }
      }
    }

    if (!isValid) return;

    try {
      this.submitButton.classList.add('loading');
      this.submitButton.disabled = true;

      //
      // Build an object whose keys are already “properties[<Name>]”.
      // Shopify will interpret each “properties[Foo]” as line‐item property “Foo.”
      //
      const props = {
        'properties[Customer Name]': this.sanitizeInput(nameInput.value),
        'properties[Date of Birth]': this.sanitizeInput(dobInput.value),
        'properties[Person of Interest]': this.sanitizeInput(targetNameInput.value),
        'properties[Person DOB]': this.sanitizeInput(targetDobInput.value)
      };
      if (detailsInput) {
        props['properties[Additional Details]'] = this.sanitizeInput(detailsInput.value);
      }
      // Get current variant's question count for submission
      const currentProductType = this.getProductType(this.variantTitle);
      if (typeof currentProductType === 'object' && currentProductType.type === 'tarot') {
        for (let i = 1; i <= currentProductType.questionCount; i++) {
          const questionInput = this.shadowRoot.getElementById(`question${i}`);
          props[`properties[Question ${i}]`] = this.sanitizeInput(questionInput.value);
        }
      }

      const shopifyRoot = window.Shopify?.routes?.root || '/';

      //
      // ————— If we’re editing an existing line, do a “POST /cart/change.js” with FormData —————
      //
      const formData = new FormData();
      // 1) always append “id”
      formData.append('id', this.currentItemKey);
      // 2) append quantity (leave unchanged). Shopify will keep existing quantity if unchanged.
      formData.append('quantity', '1');
      // 3) append each “properties[…]” pair:
      for (let [k, v] of Object.entries(props)) {
        formData.append(k, v);
      }

      const response = await fetch(`${shopifyRoot}cart/change.js`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to update cart');
      }

      // 5) update UI exactly as before
      const cartData = await response.json();
      this.hideModal();
      this.customForm.reset();
      this.currentItemKey = null;
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      } else {
        window.location.reload();
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));

    } catch (error) {
      console.error('Error:', error);
      const existingError = this.submitButton.previousElementSibling;
      if (existingError?.classList.contains('error-message')) {
        existingError.remove();
      }
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-message show';
      errorDiv.textContent = error.message || 'Failed to update cart. Please try again.';
      this.submitButton.parentNode.insertBefore(errorDiv, this.submitButton);
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitButton.disabled = false;
    }
  }

  showModal() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.previousActiveElement = document.activeElement;
    const firstInput = this.shadowRoot.querySelector('input');
    setTimeout(() => firstInput?.focus(), 100);
    this.modal.style.zIndex = '999999';
  }

  hideModal() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    this.previousActiveElement?.focus();
    // Clear error messages.
    this.shadowRoot.querySelectorAll('.error-message.show')
      .forEach(error => error.classList.remove('show'));
  }

  // This method updates the form fields based on a new set of tags and variant.
  updateFormType(tags, variantTitle = null) {
    this.productTags = tags;
    this.variantTitle = variantTitle; // Store variant title for question count
    const productType = this.getProductType(variantTitle);
    let formFields = '';

    if (
      productType === 'spell' ||
      productType === 'custom_spell' ||
      productType === 'cbtm_spell' ||
      productType === 'regular'
    ) {
      formFields = `
        <div class="form-row">
          <label for="customerName">Your Name</label>
          <input 
            type="text" 
            id="customerName" 
            name="customerName" 
            required 
            maxlength="100" 
            autocomplete="name" 
            placeholder="Enter your full name"
          >
          <div class="error-message" id="nameError">Please enter your name</div>
        </div>
        <div class="form-row">
          <label for="customerDOB">Date of Birth</label>
          <input 
            type="text" 
            id="customerDOB" 
            name="customerDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="dobError">Invalid date format</div>
        </div>
        <div class="form-row">
          <label for="targetName">Person of Interest</label>
          <input 
            type="text" 
            id="targetName" 
            name="targetName" 
            maxlength="100" 
            placeholder="Enter their full name"
          >
        </div>
        <div class="form-row">
          <label for="targetDOB">Date of Birth</label>
          <input 
            type="text" 
            id="targetDOB" 
            name="targetDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="targetDobError">Invalid date format</div>
        </div>
        <div class="form-row">
          <label for="additionalDetails">Additional Details</label>
          <textarea 
            id="additionalDetails" 
            name="additionalDetails" 
            maxlength="500" 
            placeholder="Any specific details or requests..."
          ></textarea>
          <div class="error-message" id="detailsError"></div>
        </div>
      `;
    } else if (typeof productType === 'object' && productType.type === 'tarot') {
      formFields = `
        <div class="form-row">
          <label for="customerName">Your Name</label>
          <input 
            type="text" 
            id="customerName" 
            name="customerName" 
            required 
            maxlength="100" 
            autocomplete="name" 
            placeholder="Enter your full name"
          >
          <div class="error-message" id="nameError">Please enter your name</div>
        </div>
        <div class="form-row">
          <label for="customerDOB">Date of Birth</label>
          <input 
            type="text" 
            id="customerDOB" 
            name="customerDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="dobError">Invalid date format</div>
        </div>
        <div class="form-row">
          <label for="targetName">Person of Interest</label>
          <input 
            type="text" 
            id="targetName" 
            name="targetName" 
            maxlength="100" 
            placeholder="Enter their full name"
          >
        </div>
        <div class="form-row">
          <label for="targetDOB">Date of Birth</label>
          <input 
            type="text" 
            id="targetDOB" 
            name="targetDOB" 
            placeholder="DD/MM/YYYY"
          >
          <div class="error-message" id="targetDobError">Invalid date format</div>
        </div>
        ${this.createTarotFields(productType.questionCount, {})}
      `;
    }

    // Append the submit button.
    formFields += `
      <button type="submit" class="submit-button">
        <span class="spinner" aria-hidden="true"></span>
        <span class="button-text">Update Cart</span>
      </button>
    `;

    const form = this.shadowRoot.getElementById('CustomCartForm');
    if (form) {
      form.innerHTML = formFields;
      this.setupEventListeners();
    }
  }

  // Populate the form with existing personalization data and show the modal.
  editItem(properties) {
    Object.entries(properties).forEach(([key, value]) => {
      switch(key) {
        case 'Customer Name':
          this.shadowRoot.getElementById('customerName').value = value;
          break;
        case 'Date of Birth':
          this.shadowRoot.getElementById('customerDOB').value = value;
          break;
        case 'Person of Interest':
          this.shadowRoot.getElementById('targetName').value = value;
          break;
        case 'Person DOB':
          this.shadowRoot.getElementById('targetDOB').value = value;
          break;
        case 'Additional Details':
          const detailsInput = this.shadowRoot.getElementById('additionalDetails');
          if (detailsInput) detailsInput.value = value;
          break;
        default:
          if (key.startsWith('Question')) {
            const questionNum = key.replace('Question ', '');
            const questionInput = this.shadowRoot.getElementById(`question${questionNum}`);
            if (questionInput) questionInput.value = value;
          }
          break;
      }
    });
    this.showModal();
  }
}

customElements.define('cart-form-modal', CartFormModal);
